import { useRef, useState } from 'react';
import Donut from '../components/Donut';
import PieLegend from '../components/PieLegend';
import { updatePortfolio, updateProfile } from '../data/userService';
import { deriveAssetKey, formatPct, holdingColor, pieLabel, toNumber, totalPct } from '../utils/portfolio';

/**
 * Optional step. "Save portfolio" needs holdings that add up to 100%.
 * "Skip for now" records portfolioSetup: 'skipped' so we don't ask again;
 * they can add it later from Update portfolio.
 */
export default function PortfolioStep({ uid, initial, firstName, onDone }) {
  const nextId = useRef(0);
  const newRow = (h = {}) => ({
    id: nextId.current++,
    name: h.name || '',
    ticker: h.ticker || '',
    pct: h.pct != null ? String(h.pct) : '',
    why: h.why || '',
  });

  const [rows, setRows] = useState(() =>
    initial?.holdings?.length ? initial.holdings.map(newRow) : [newRow(), newRow()]
  );
  const [ret, setRet] = useState(initial?.return1y ? String(initial.return1y) : '');
  const [busy, setBusy] = useState(null); // 'save' | 'skip' | null
  const [error, setError] = useState('');

  const total = totalPct(rows);
  const pieRows = rows.map((r) => ({ ...r, ticker: r.ticker.trim().toUpperCase() })); // tickers in caps in the key
  const isComplete = Math.abs(total - 100) < 0.05;
  const status = isComplete
    ? 'Adds up to 100%. Ready to save.'
    : total === 0
      ? 'Add your holdings and what % of your portfolio each one is.'
      : total < 100
        ? `${formatPct(100 - total)} left to allocate`
        : `Over by ${formatPct(total - 100)}`;

  const setRow = (id, field) => (e) =>
    setRows(rows.map((r) => (r.id === id ? { ...r, [field]: e.target.value } : r)));
  const removeRow = (id) => setRows(rows.filter((r) => r.id !== id));

  const save = async () => {
    if (!isComplete) return;
    setBusy('save');
    setError('');
    const holdings = rows
      .filter((r) => (r.name || r.ticker) && toNumber(r.pct) > 0)
      .map((r) => {
        const ticker = r.ticker.trim().toUpperCase();
        const label = r.name.trim() || ticker;
        return { label, ticker, assetKey: deriveAssetKey({ name: label, ticker }), pct: toNumber(r.pct), why: r.why.trim() };
      });
    try {
      await updatePortfolio(uid, {
        holdings,
        value: 0,
        return1y: toNumber(ret),
        showValue: false, // they can choose to show £ later in Update portfolio
        updatedAt: new Date().toISOString(),
      });
      await updateProfile(uid, { portfolioSetup: 'done' });
      onDone('done');
    } catch (err) {
      console.error('Saving portfolio failed', err);
      setError('We couldn’t save your portfolio. Check your connection and try again.');
      setBusy(null);
    }
  };

  const skip = async () => {
    setBusy('skip');
    setError('');
    try {
      await updateProfile(uid, { portfolioSetup: 'skipped' });
      onDone('skipped');
    } catch (err) {
      console.error('Skipping portfolio failed', err);
      setError('Something went wrong. Check your connection and try again.');
      setBusy(null);
    }
  };

  return (
    <div className="ob-body">
      <div className="ob-intro">
        <h2 id="ob-title">{firstName ? `Nice to meet you, ${firstName}. ` : ''}Add your portfolio</h2>
        <p>
          Members follow each other for the pie and the reasons behind it. Add what you hold and roughly what
          share each is. You can skip this and add it later.
        </p>
      </div>

      <div className="ob-pf">
        <div className="ob-pf__rows">
          {rows.map((r, i) => (
            <div key={r.id} className="ob-holding">
              <span className="ob-holding__dot" style={{ background: holdingColor(r, i) }} aria-hidden="true" />
              <div className="ob-holding__grid">
                <div className="ob-field ob-h-name">
                  <label htmlFor={`ob-h-name-${r.id}`}>Fund or stock</label>
                  <input id={`ob-h-name-${r.id}`} placeholder="e.g. Vanguard FTSE All-World" value={r.name} onChange={setRow(r.id, 'name')} />
                </div>
                <div className="ob-field">
                  <label htmlFor={`ob-h-t-${r.id}`}>Ticker</label>
                  <input id={`ob-h-t-${r.id}`} className="ob-upper" placeholder="VWRP" value={r.ticker} onChange={setRow(r.id, 'ticker')} />
                </div>
                <div className="ob-field">
                  <label htmlFor={`ob-h-p-${r.id}`}>%</label>
                  <input id={`ob-h-p-${r.id}`} className="ob-right" type="number" inputMode="decimal" min="0" max="100" step="0.5" value={r.pct} onChange={setRow(r.id, 'pct')} />
                </div>
                <div className="ob-field ob-h-why">
                  <label htmlFor={`ob-h-w-${r.id}`}>Why do you hold it? <span className="ob-opt">optional</span></label>
                  <input id={`ob-h-w-${r.id}`} placeholder="A sentence on why it’s in your portfolio" value={r.why} onChange={setRow(r.id, 'why')} />
                </div>
                <button type="button" className="ob-remove" aria-label={`Remove ${r.ticker || r.name || 'this holding'}`} onClick={() => removeRow(r.id)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              </div>
            </div>
          ))}
          <button type="button" className="ob-btn ob-btn--ghost ob-btn--dashed" onClick={() => setRows([...rows, newRow()])}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            Add a holding
          </button>
          <div className="ob-field ob-return">
            <label htmlFor="ob-return">1-year return (%) <span className="ob-opt">optional</span></label>
            <input id="ob-return" inputMode="decimal" placeholder="e.g. 8.5" value={ret} onChange={(e) => setRet(e.target.value)} />
          </div>
        </div>

        <aside className="ob-pf__pie" aria-label="Your pie">
          <Donut holdings={pieRows} size={160} ring={24} label={pieLabel('Your', pieRows)}>
            <span className="donut__big">{formatPct(total)}</span>
            <span className="donut__small">allocated</span>
          </Donut>
          <p role="status" className={`ob-status${isComplete ? ' ob-status--ok' : ''}`}>{status}</p>
          <PieLegend holdings={pieRows} />
        </aside>
      </div>

      {error && <p className="ob-error" role="alert">{error}</p>}

      <div className="ob-actions">
        <button type="button" className="ob-btn ob-btn--ghost" onClick={skip} disabled={Boolean(busy)}>
          {busy === 'skip' ? 'One moment…' : 'Skip for now'}
        </button>
        <button type="button" className="ob-btn ob-btn--primary" onClick={save} disabled={!isComplete || Boolean(busy)}>
          {busy === 'save' ? 'Saving…' : 'Save portfolio'}
        </button>
      </div>
    </div>
  );
}
